import { ROUTES, STORAGE_KEYS } from "../constants";
import { getPostLoginRedirectPath } from "../permissions/postLoginRedirect";

const AUTH_ROUTE_PREFIXES = [
  ROUTES.LOGIN,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.RESET_PASSWORD,
  ROUTES.VERIFY_EMAIL,
  ROUTES.VERIFY_EMAIL_SUCCESS,
  ROUTES.FORCE_PASSWORD,
  ROUTES.MFA_LOGIN,
  ROUTES.MFA_SETUP,
];

/**
 * @param {string | null | undefined} url — path + search + hash only (must start with `/`)
 * @returns {string | null}
 */
export function sanitizeAuthReturnUrl(url) {
  if (url == null || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return null;

  const pathOnly = trimmed.split(/[?#]/)[0] || trimmed;
  const isAuthRoute = AUTH_ROUTE_PREFIXES.some(
    (route) => pathOnly === route || pathOnly.startsWith(`${route}/`)
  );
  if (isAuthRoute) return null;

  return trimmed;
}

/**
 * @param {{ pathname?: string, search?: string, hash?: string } | string | null | undefined} locationLike
 * @returns {string | null}
 */
export function locationToReturnPath(locationLike) {
  if (locationLike == null) return null;
  if (typeof locationLike === "string") return sanitizeAuthReturnUrl(locationLike);
  const path = `${locationLike.pathname ?? ""}${locationLike.search ?? ""}${locationLike.hash ?? ""}`;
  return sanitizeAuthReturnUrl(path);
}

/** Persist intended destination for post-login (survives MFA steps). */
export function saveAuthReturnUrl(locationLike) {
  const safe = locationToReturnPath(locationLike);
  if (!safe) return;
  try {
    sessionStorage.setItem(STORAGE_KEYS.AUTH_RETURN_URL, safe);
  } catch {
    /* quota / private mode */
  }
}

export function peekAuthReturnUrl() {
  try {
    return sanitizeAuthReturnUrl(sessionStorage.getItem(STORAGE_KEYS.AUTH_RETURN_URL));
  } catch {
    return null;
  }
}

export function consumeAuthReturnUrl() {
  const value = peekAuthReturnUrl();
  try {
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_RETURN_URL);
  } catch {
    /* ignore */
  }
  return value;
}

/**
 * After login: open the URL the user originally requested, else role default landing.
 * @param {Record<string, unknown> | null | undefined} me
 * @param {{ state?: { from?: { pathname?: string, search?: string, hash?: string } } } | null | undefined} location
 */
export function resolvePostLoginNavigatePath(me, location) {
  const fromState = locationToReturnPath(location?.state?.from);
  if (fromState) {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.AUTH_RETURN_URL);
    } catch {
      /* ignore */
    }
    return fromState;
  }

  const stored = consumeAuthReturnUrl();
  if (stored) return stored;

  return getPostLoginRedirectPath(me);
}
